import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Download, Eye, Plus, Mail, Copy, Check, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TodayBlock } from "@/components/ProspectsToday";
import { ReplyDialog } from "@/components/ProspectReply";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({ "X-Admin-Token": localStorage.getItem("oz_admin_token") || "" });
const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");
const STATUS_COLOR = {
  "à vérifier": "bg-gray-100 text-gray-700", "qualifié": "bg-blue-50 text-blue-800", "prêt à contacter": "bg-amber-100 text-amber-900",
  "contacté": "bg-indigo-50 text-indigo-800", "réponse reçue": "bg-purple-50 text-purple-800", "intéressé": "bg-green-100 text-green-800",
  "non intéressé": "bg-red-50 text-red-700", "ne plus contacter": "bg-red-100 text-red-800",
};

const Stats = ({ items }) => {
  const c = (s) => items.filter((p) => p.status === s).length;
  const cells = [["Prospects", items.length], ["E-mail vérifié", items.filter((p) => p.email_verified).length],
    ["Prêts à contacter", c("prêt à contacter") + c("qualifié")], ["Contactés", c("contacté")],
    ["Réponses", c("réponse reçue") + c("intéressé") + c("non intéressé")], ["Intéressés", c("intéressé")], ["Ne plus contacter", c("ne plus contacter")]];
  return (
    <div data-testid="prospects-stats" className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
      {cells.map(([l, v]) => (
        <div key={l} className="rounded-xl bg-white border border-[var(--oz-border)] p-3">
          <p className="text-xs text-[var(--oz-muted)]">{l}</p><p className="text-xl font-semibold">{v}</p>
        </div>
      ))}
    </div>
  );
};

const AddForm = ({ onDone }) => {
  const [f, setF] = useState({ name: "", category: "salon", city: "", country: "France", email: "", phone: "", website: "", status: "à vérifier" });
  const save = async () => {
    try { await axios.post(`${API}/admin/prospects`, f, { headers: headers() }); toast.success("Prospect ajouté"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const inp = "rounded-lg border border-[var(--oz-border)] px-3 py-2 text-sm";
  return (
    <div data-testid="prospect-add-form" className="grid sm:grid-cols-3 gap-2 rounded-xl bg-white border border-[var(--oz-border)] p-4">
      {[["name", "Nom"], ["city", "Ville"], ["country", "Pays"], ["email", "E-mail pro"], ["phone", "Téléphone pro (+indicatif)"], ["website", "Site internet"]].map(([k, l]) => (
        <input key={k} placeholder={l} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`prospect-add-${k}`} className={inp} />
      ))}
      <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="prospect-add-category" className={inp}>
        {["salon", "salon_europeen", "independant", "barbier", "boutique", "cosmetiques", "grossiste", "partenaire", "ecole"].map((c) => <option key={c}>{c}</option>)}
      </select>
      <button onClick={save} data-testid="prospect-add-save" className="rounded-full bg-[var(--oz-dark)] text-white text-sm font-semibold px-4 py-2">Ajouter</button>
    </div>
  );
};

const Row = ({ p, statuses, onChange, onPreview, onWrite, onReply }) => (
  <tr data-testid={`prospect-row-${p.id}`} className="border-t border-[var(--oz-border)] align-top">
    <td className="p-2">
      <button onClick={onWrite} disabled={(!p.email && !p.phone) || p.status === "ne plus contacter"} data-testid={`prospect-write-${p.id}`}
        className="inline-flex items-center gap-1 rounded-full bg-[var(--oz-dark)] text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-30">
        <Mail className="w-3.5 h-3.5 text-[var(--oz-gold)]" /> {p.status === "contacté" ? "Relancer" : "Écrire"}
      </button>
    </td>
    <td className="p-2 min-w-[180px]">
      <p className="font-semibold">{p.name}</p>
      <p className="text-xs text-[var(--oz-muted)]">{p.category_label || p.category} · {p.city}{p.country && p.country !== "France" ? ` (${p.country})` : ""}</p>
      {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="text-xs underline text-[var(--oz-muted)]">site</a>}
    </td>
    <td className="p-2 text-xs min-w-[190px] max-w-[240px] break-all">
      {p.phone && <span className="block">{p.phone}</span>}
      {p.email || (p.phone ? "" : "—")}{p.email && <span className={`ml-1 ${p.email_verified ? "text-green-700" : "text-amber-700"}`}>{p.email_verified ? "✓ vérifié" : "à confirmer"}</span>}
    </td>
    <td className="p-2">
      <select value={p.status} onChange={(e) => onChange({ status: e.target.value })} data-testid={`prospect-status-${p.id}`}
        className={`rounded-full px-2 py-1 text-xs font-semibold border-0 ${STATUS_COLOR[p.status] || ""}`}>
        {statuses.map((s) => <option key={s}>{s}</option>)}
      </select>
    </td>
    <td className="p-2 text-xs whitespace-nowrap">{fmt(p.first_contact_at)}<br /><span className="text-[var(--oz-muted)]">Relance : {fmt(p.followup_at)}</span></td>
    <td className="p-2 text-xs min-w-[180px]">
      <input key={p.result || ""} defaultValue={p.result || ""} placeholder="Résultat / réponse" onBlur={(e) => e.target.value !== (p.result || "") && onChange({ result: e.target.value })}
        data-testid={`prospect-result-${p.id}`} className="w-full rounded border border-[var(--oz-border)] px-2 py-1" />
      <p className="mt-1 text-[var(--oz-muted)]">{p.next_action}</p>
    </td>
    <td className="p-2 whitespace-nowrap"><button onClick={onReply} data-testid={`prospect-reply-${p.id}`} className="mr-1 rounded-full border border-[var(--oz-border)] px-2.5 py-1 text-xs font-semibold hover:bg-black/5">Réponse reçue</button><button onClick={onPreview} disabled={!p.email} data-testid={`prospect-preview-${p.id}`} className="p-2 rounded-full hover:bg-black/5 disabled:opacity-30"><Eye className="w-4 h-4" /></button></td>
  </tr>
);

export default function AdminProspects() {
  const [data, setData] = useState({ items: [], statuses: [] });
  const [msg, setMsg] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [tick, setTick] = useState(0);
  const [filter, setFilter] = useState("tous");
  const [cat, setCat] = useState("toutes");
  const [country, setCountry] = useState("tous");
  const [prev, setPrev] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = () => axios.get(`${API}/admin/prospects`, { headers: headers() }).then(({ data }) => setData(data))
    .catch((e) => toast.error(e.response?.status === 401 ? "Connectez-vous d'abord sur /admin" : "Chargement impossible"));
  useEffect(() => { load(); }, []);

  const items = useMemo(() => data.items.filter((p) => (filter === "tous" || p.status === filter) && (cat === "toutes" || p.category === cat) && (country === "tous" || (p.country || "France") === country)), [data, filter, cat, country]);
  const countries = useMemo(() => [...new Set(data.items.map((p) => p.country || "France"))].sort(), [data]);
  const due = data.items.filter((p) => p.status === "contacté" && p.followup_at && new Date(p.followup_at) <= new Date());

  const update = async (id, body) => {
    try { await axios.put(`${API}/admin/prospects/${id}`, body, { headers: headers() }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const preview = async (p, followup = false) => {
    try {
      const { data: d } = await axios.get(`${API}/admin/prospects/${p.id}/preview?followup=${followup}`, { headers: headers() });
      setPrev(d);
    } catch { toast.error("Aperçu impossible"); }
  };
  const write = async (p) => {
    const followup = p.status === "contacté";
    try {
      const { data: d } = await axios.get(`${API}/admin/prospects/${p.id}/message?followup=${followup}`, { headers: headers() });
      setMsg({ ...d, id: p.id, followup, copied: false });
    } catch (e) { toast.error(e.response?.data?.detail || "Message indisponible"); }
  };
  const markSent = async (channel = "e-mail") => {
    try {
      await axios.post(`${API}/admin/prospects/${msg.id}/mark-sent`, { followup: msg.followup, channel }, { headers: headers() });
      toast.success("Prospect marqué comme contacté"); setMsg(null); load(); setTick((t) => t + 1);
    } catch { toast.error("Erreur"); }
  };
  const copyMsg = async () => {
    try { await navigator.clipboard.writeText(`${msg.subject}\n\n${msg.body}`); setMsg({ ...msg, copied: true }); }
    catch { toast("Sélectionnez le texte pour le copier"); }
  };
  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/prospects/export.csv`, { headers: headers(), responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "prospects-ozentya.csv"; a.click();
    } catch { toast.error("Export impossible"); }
  };

  const btn = "inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold disabled:opacity-50";
  return (
    <div data-testid="admin-prospects-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-[var(--oz-muted)]"><ArrowLeft className="w-4 h-4" /> Administration</Link>
      <h1 className="mt-3 font-serif text-3xl sm:text-4xl">Prospection</h1>
      <p className="mt-2 text-sm text-[var(--oz-muted)] max-w-3xl">Salons, coiffeurs, barbiers, boutiques et grossistes afro. Cliquez sur « Écrire » : le message personnalisé s'ouvre dans votre propre messagerie, vous l'envoyez, puis « Marquer comme envoyé ». Si un contact répond « STOP », passez-le en « ne plus contacter ».</p>
      <div className="mt-6"><Stats items={data.items} /></div>

      <TodayBlock headers={headers} onWrite={write} refreshKey={tick} />
      <div className="mt-6 flex flex-wrap gap-2">
        <button onClick={() => setFilter("contacté")} data-testid="prospects-followups" className={`${btn} border border-[var(--oz-border)]`}>
          <RotateCcw className="w-4 h-4" /> Relances dues ({due.length})
        </button>
        <button onClick={exportCsv} data-testid="prospects-export" className={`${btn} border border-[var(--oz-border)]`}><Download className="w-4 h-4" /> Export tableur</button>
        <button onClick={() => setAdding((v) => !v)} data-testid="prospects-add-toggle" className={`${btn} border border-[var(--oz-border)]`}><Plus className="w-4 h-4" /> Ajouter</button>
      </div>
      {adding && <div className="mt-4"><AddForm onDone={() => { setAdding(false); load(); }} /></div>}

      <div className="mt-6 flex flex-wrap gap-2 text-sm">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="prospects-filter-status" className="rounded-full border border-[var(--oz-border)] px-3 py-2">
          <option value="tous">Tous les statuts</option>{data.statuses.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="prospects-filter-category" className="rounded-full border border-[var(--oz-border)] px-3 py-2">
          <option value="toutes">Toutes les catégories</option>
          {["salon", "salon_europeen", "independant", "barbier", "boutique", "cosmetiques", "grossiste", "partenaire", "ecole"].map((c) => <option key={c}>{c}</option>)}
        </select>
        <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="prospects-filter-country" className="rounded-full border border-[var(--oz-border)] px-3 py-2">
          <option value="tous">Tous les pays</option>{countries.map((c) => <option key={c}>{c}</option>)}
        </select>
        <span className="self-center text-[var(--oz-muted)]">{items.length} affiché(s)</span>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl bg-white border border-[var(--oz-border)]">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-[var(--oz-muted)]">
            <tr><th className="p-2">Action</th><th className="p-2">Établissement</th><th className="p-2">E-mail</th><th className="p-2">Statut</th><th className="p-2">1er contact</th><th className="p-2">Résultat · prochaine action</th><th className="p-2" /></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <Row key={p.id} p={p} statuses={data.statuses} onWrite={() => write(p)}
                onChange={(b) => update(p.id, b)} onPreview={() => preview(p, p.status === "contacté")} onReply={() => setReplyFor(p)} />
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!msg} onOpenChange={(o) => !o && setMsg(null)}>
        <DialogContent className="max-w-2xl" data-testid="prospect-message-dialog">
          <DialogHeader><DialogTitle>{msg?.subject}</DialogTitle></DialogHeader>
          <p className="text-xs text-[var(--oz-muted)]">À : {msg?.to || "—"}{msg?.phone ? ` · ${msg.phone}` : ""} · langue : {msg?.language}</p>
          <pre data-testid="prospect-message-body" className="whitespace-pre-wrap text-sm font-sans max-h-[45vh] overflow-y-auto rounded-lg bg-[var(--oz-bg)] p-3">{msg?.body}</pre>
          <div className="flex flex-wrap gap-2">
            {msg?.to && <a href={`mailto:${msg.to}?subject=${encodeURIComponent(msg.subject)}&body=${encodeURIComponent(msg.body)}`} data-testid="prospect-message-open"
              className={`${btn} bg-[var(--oz-dark)] text-white`}><Mail className="w-4 h-4 text-[var(--oz-gold)]" /> Ouvrir dans ma messagerie</a>}
            {msg?.phone && <a href={`https://wa.me/${msg.phone.replace(/\D/g, "").replace(/^00/, "").replace(/^0/, "33")}?text=${encodeURIComponent(msg.whatsapp_text)}`} target="_blank" rel="noreferrer"
              onClick={() => setMsg({ ...msg, wa: true })} data-testid="prospect-message-whatsapp" className={`${btn} bg-[#25D366] text-[#0B2E13]`}>WhatsApp</a>}
            <button onClick={copyMsg} data-testid="prospect-message-copy" className={`${btn} border border-[var(--oz-border)]`}>
              {msg?.copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} Copier
            </button>
            <button onClick={() => markSent(msg?.wa ? "whatsapp" : "e-mail")} data-testid="prospect-message-mark-sent" className={`${btn} bg-[var(--oz-gold)] text-[var(--oz-dark)]`}>Marquer comme envoyé</button>
          </div>
        </DialogContent>
      </Dialog>

      {replyFor && <ReplyDialog p={replyFor} statuses={data.statuses} headers={headers} onClose={() => { setReplyFor(null); load(); }} onSaved={() => { setReplyFor(null); load(); setTick((t) => t + 1); }} />}

      <Dialog open={!!prev} onOpenChange={(o) => !o && setPrev(null)}>
        <DialogContent className="max-w-2xl" data-testid="prospect-preview-dialog">
          <DialogHeader><DialogTitle>{prev?.subject}</DialogTitle></DialogHeader>
          <p className="text-xs text-[var(--oz-muted)]">À : {prev?.to}</p>
          <iframe title="aperçu" srcDoc={prev?.html} className="w-full h-[60vh] rounded-lg border border-[var(--oz-border)]" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
